import React, { useEffect, useState } from 'react';
import CreatableSelect from 'react-select/creatable';

import CustomDropdown from '@/components/CustomDropdown';
import Editor from '@/components/editor/Editor';
import { FILTER_STATUS } from '@/features/task-configuration/constants';
import { useTaskConfigurations } from '@/store/reducers';
import {
	useAddTaskDashboardFilterMutation,
	useUpdateTaskDashboardFilterMutation,
	useGetTaskWithDependencyQuery,
	useGetFilterLogicExecutorsQuery,
	useGetTaskDashboardFilterByIdQuery
} from '@/features/task-configuration/api/taskConfigurationApi';
import { toastError, toastSuccess } from '@/components/Toast';
import CommonImage from '@/components/CommonImage';
import { useGetEnumsQuery } from '@/api/commonApis';
import { getErrorMessage } from '@/utils/Util';
import SopViewModal from '@/features/task-configuration/components/SopViewModal';
import { useGetSopListQuery } from '@/features/task-configuration/api/taskConfigurationApi';

// Component to handle logic executors per row
const LogicExecutorsField = ({ row, index, handleRowChange, setRowMeta }) => {
	const filterTopicKey = row.filterTopic?.id ?? row.filterTopic?.value ?? '';
	const operatorKey = row.filterLogicOperator?.id ?? row.filterLogicOperator?.value ?? '';

	const { data: logicExecutorsResponse, isLoading } = useGetFilterLogicExecutorsQuery(
		{
			filterTopic: filterTopicKey,
			params: operatorKey ? { operator: operatorKey } : undefined
		},
		{
			skip: !filterTopicKey || !operatorKey,
			// Ensure we always hit the API when topic/operator changes (even if cached).
			refetchOnMountOrArgChange: true
		}
	);

	// Extract dropdownList and inputFieldType from API response
	const dropdownList = React.useMemo(() => {
		if (!logicExecutorsResponse) return [];
		if (Array.isArray(logicExecutorsResponse)) return logicExecutorsResponse;
		if (logicExecutorsResponse.dropdownList && Array.isArray(logicExecutorsResponse.dropdownList)) {
			return logicExecutorsResponse.dropdownList;
		}
		if (logicExecutorsResponse.data && Array.isArray(logicExecutorsResponse.data)) {
			return logicExecutorsResponse.data;
		}
		return [];
	}, [logicExecutorsResponse]);

	const inputFieldType = React.useMemo(() => {
		if (!logicExecutorsResponse) return null;
		// API can legitimately return empty string meaning "no input expected".
		if (Object.prototype.hasOwnProperty.call(logicExecutorsResponse, 'inputFieldType')) {
			return logicExecutorsResponse.inputFieldType;
		}
		return 'multi-select';
	}, [logicExecutorsResponse]);

	// Determine if it's a number input based on API response
	const isNumericInput = inputFieldType === 'number';
	const isDisabledByApi = inputFieldType === '';

	// Persist the inputFieldType on the row for validation and downstream logic.
	React.useEffect(() => {
		if (!setRowMeta) return;
		setRowMeta(index, { logicExecutorsInputFieldType: inputFieldType });
	}, [index, inputFieldType]);

	// If API says no input expected, clear executors and disable the field.
	React.useEffect(() => {
		if (!setRowMeta) return;
		if (!isDisabledByApi) return;
		if (row.logicExecutors && row.logicExecutors.length > 0) {
			setRowMeta(index, { logicExecutors: [] });
		}
	}, [index, isDisabledByApi, row.logicExecutors]);

	// Merge the loaded executors with API data to get proper labels
	const mergedValue = React.useMemo(() => {
		if (!row.logicExecutors || row.logicExecutors.length === 0) return [];

		return row.logicExecutors.map((executor) => {
			// Extract the actual value (handle both object and primitive)
			const executorValue = typeof executor === 'object' ? executor.value : executor;
			const executorLabel = typeof executor === 'object' ? executor.label : executor;

			// If we already have a proper label (not just the value stringified), use the existing object
			if (executorLabel && executorLabel !== executorValue?.toString()) {
				return executor;
			}

			// Otherwise, try to find the matching item from API data to get the proper label
			const apiItem = dropdownList?.find((item) => {
				// Compare as numbers for numeric values, strings otherwise
				const itemValue = item.value;
				const execValue = executorValue;

				// Try numeric comparison first
				if (!isNaN(itemValue) && !isNaN(execValue)) {
					return Number(itemValue) === Number(execValue);
				}

				// Fall back to string comparison
				return String(itemValue) === String(execValue);
			});

			// If found in API, use the API item with proper label
			if (apiItem) {
				return {
					value: apiItem.value,
					label: apiItem.label,
					id: apiItem.value
				};
			}

			// Otherwise, return the executor as-is (for cases where API hasn't loaded yet or value not in API)
			return typeof executor === 'object' ? executor : { value: executor, label: String(executor) };
		});
	}, [row.logicExecutors, dropdownList?.length]);

	// If topic or operator is not selected, render null
	if (!filterTopicKey || !operatorKey) {
		return (
			<input
				type="text"
				placeholder="Select topic and operator first"
				disabled
				style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
			/>
		);
	}

	// If loading, show disabled input
	if (isLoading) {
		return (
			<input
				type="text"
				placeholder="Loading..."
				disabled
				style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
			/>
		);
	}

	// If no response yet, render null
	if (!inputFieldType) {
		return null;
	}

	// If API returns empty inputFieldType, render disabled input
	if (isDisabledByApi) {
		return (
			<input
				type="text"
				placeholder="Not applicable"
				disabled
				style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
			/>
		);
	}

	// Render number input
	if (isNumericInput) {
		return (
			<input
				type="number"
				placeholder="Enter numeric value"
				value={row.logicExecutors?.[0]?.value || row.logicExecutors?.[0] || ''}
				onChange={(e) => handleRowChange(index, 'logicExecutors', [e.target.value])}
			/>
		);
	}

	// Render multi-select dropdown
	return (
		<CreatableSelect
			isMulti
			value={mergedValue}
			onChange={(value) => handleRowChange(index, 'logicExecutors', value || [])}
			options={dropdownList?.map((item) => ({
				...item,
				id: item?.value,
				value: item?.value,
				label: item?.label
			}))}
			isClearable={false}
			placeholder="Select"
			isValidNewOption={() => false}
		/>
	);
};

const INITIAL_STATE = {
	title: '',
	stage: null,
	willCompleteTaskList: [],
	logicDoc: '',
	queryLogic: '',
	status: { id: 'ACTIVE', name: 'Active', value: 'Active', boolean: true },
	leadTime: '',
	description: '',
	sopDetails: null,
	listViewType: '',
	rows: [
		{
			id: null,
			filterLogicTitle: '',
			filterTopic: null,
			filterLogicOperator: null,
			filterLogicDetails: [],
			logicExecutors: [],
			logicExecutorsInputFieldType: null
		},
		{
			id: null,
			filterLogicTitle: '',
			filterTopic: null,
			filterLogicOperator: null,
			filterLogicDetails: [],
			logicExecutors: [],
			logicExecutorsInputFieldType: null
		},
		{
			id: null,
			filterLogicTitle: '',
			filterTopic: null,
			filterLogicOperator: null,
			filterLogicDetails: [],
			logicExecutors: [],
			logicExecutorsInputFieldType: null
		}
	]
};

const generalListViewRow = (enums) =>
	enums?.find(
		(i) =>
			i.name?.toLowerCase() === 'general' ||
			i.constant === 'GENERAL' ||
			i.constant?.toLowerCase() === 'general'
	);

const listViewTypeSelectedName = (enums, constant) =>
	enums?.find((i) => i.constant === constant)?.name ?? generalListViewRow(enums)?.name ?? '';

const PrimarySettings = ({ setOpenPrimarySettings, selectedRowToEdit, setSelectedRowToEdit }) => {
	const [formData, setFormData] = useState(INITIAL_STATE);
	const [errors, setErrors] = useState(null);
	const [logicDocContent, setLogicDocContent] = useState(null);
	const [isDataLoaded, setIsDataLoaded] = useState(false);
	const [sopModalOpen, setSopModalOpen] = useState(false);

	// Row meta updates that should NOT trigger row auto-add behavior.
	const setRowMeta = (index, patch) => {
		setFormData((prev) => {
			const updatedRows = [...prev.rows];
			updatedRows[index] = { ...updatedRows[index], ...patch };
			return { ...prev, rows: updatedRows };
		});
	};

	const { stageList = [] } = useTaskConfigurations();
	const { data: sopList = [] } = useGetSopListQuery();
	const { data: taskList } = useGetTaskWithDependencyQuery();
	const [addTaskDashboardFilter] = useAddTaskDashboardFilterMutation();
	const [updateTaskDashboardFilter] = useUpdateTaskDashboardFilterMutation();
	const { data: filterTopics } = useGetEnumsQuery({
		path: 'info.nitex.app.dashboard.TaskDashboardFilterTopic'
	});
	const { data: logicOperators } = useGetEnumsQuery({
		path: 'info.nitex.app.dashboard.TaskDashboardLogicOperator'
	});
	const { data: taskDashboardFilterById } = useGetTaskDashboardFilterByIdQuery(
		{ id: selectedRowToEdit?.id },
		{ skip: !selectedRowToEdit?.id }
	);
	const { data: taskListType } = useGetEnumsQuery({
		path: 'info.nitex.app.dashboard.TaskDashboardFilterListViewType'
	});

	const resetForm = React.useCallback(() => {
		setFormData({
			...INITIAL_STATE,
			listViewType: generalListViewRow(taskListType)?.constant ?? INITIAL_STATE.listViewType
		});
		setErrors(null);
		setLogicDocContent(null);
		// In create-mode, editor should render immediately
		setIsDataLoaded(true);
	}, [taskListType]);

	// Create mode: default listViewType to General when enums load
	useEffect(() => {
		if (selectedRowToEdit?.id || !taskListType?.length) return;
		const constant = generalListViewRow(taskListType)?.constant;
		if (!constant) return;
		setFormData((prev) => (prev.listViewType ? prev : { ...prev, listViewType: constant }));
	}, [selectedRowToEdit?.id, taskListType]);

	// Reset when switching records or opening create mode
	useEffect(() => {
		if (!selectedRowToEdit?.id) {
			// Create mode - set data as loaded immediately
			setIsDataLoaded(true);
			setLogicDocContent(null);
		} else {
			// Edit mode - wait for data
			setIsDataLoaded(false);
		}
	}, [selectedRowToEdit?.id]);

	useEffect(() => {
		if (taskDashboardFilterById && filterTopics && logicOperators) {
			// eslint-disable-next-line no-console
			console.log('taskDashboardFilterById:', taskDashboardFilterById);

			// Map the edit data to form data structure
			const statusObj = FILTER_STATUS.find((item) => item.boolean === taskDashboardFilterById.isActive);

			// Build a lookup map for all tasks from taskCompletionAllowed and their dependent tasks
			const taskLookup = {};

			// Helper function to recursively extract all tasks
			const extractTasks = (taskList) => {
				if (!taskList || !Array.isArray(taskList)) return;
				taskList.forEach((task) => {
					if (task.id && task.name) {
						taskLookup[task.id] = task.name;
					}
					// Recursively extract from dependentTaskList
					if (task.dependentTaskList && task.dependentTaskList.length > 0) {
						extractTasks(task.dependentTaskList);
					}
				});
			};

			// Extract all tasks from the response
			extractTasks(taskDashboardFilterById.taskCompletionAllowed);

			// Map logic items if they exist, otherwise use initial 3 rows
			const mappedRows =
				taskDashboardFilterById.logicItemResponses?.length > 0
					? taskDashboardFilterById.logicItemResponses.map((item) => {
							// Find the matching topic enum to get the formatted name
							const topicEnum = filterTopics?.find((t) => t.constant === item.topic);
							// Find the matching operator enum to get the formatted name
							const operatorEnum = logicOperators?.find((o) => o.constant === item.operator);

							// Map associatedEntityIds to logicExecutors with proper labels from response data
							const logicExecutors =
								item.associatedEntityIds?.map((id) => {
									// Try to find the label from our task lookup
									const label = taskLookup[id] || id.toString();
									return {
										value: id,
										label: label
									};
								}) || [];

							return {
								id: item.id,
								filterLogicTitle: item.name || '',
								filterTopic: topicEnum
									? {
											id: topicEnum.constant,
											value: topicEnum.name,
											name: topicEnum.name
										}
									: null,
								filterLogicOperator: operatorEnum
									? {
											id: operatorEnum.constant,
											value: operatorEnum.name,
											name: operatorEnum.name
										}
									: null,
								filterLogicDetails: item.associatedEntityIds?.join(', ') || '',
								logicExecutors: logicExecutors
							};
						})
					: INITIAL_STATE.rows;

			setFormData({
				title: taskDashboardFilterById.title || '',
				stage: taskDashboardFilterById.category
					? {
							id: taskDashboardFilterById.category.id,
							value: taskDashboardFilterById.category.name,
							name: taskDashboardFilterById.category.name
						}
					: null,
				willCompleteTaskList:
					taskDashboardFilterById.taskCompletionAllowed?.map((item) => ({
						...item,
						value: item.name,
						label: item.name
					})) || [],
				logicDoc: taskDashboardFilterById.logicDocumentation || '',
				queryLogic: taskDashboardFilterById.logic || '',
				status: statusObj || null,
				leadTime: taskDashboardFilterById.leadTime || '',
				description: taskDashboardFilterById.details || '',
				sopDetails: taskDashboardFilterById.sopResponse
					? {
							id: taskDashboardFilterById.sopResponse.id,
							value: taskDashboardFilterById.sopResponse.title,
							name: taskDashboardFilterById.sopResponse.title
						}
					: null,
				listViewType:
					taskDashboardFilterById.listViewType?.constant || taskDashboardFilterById.listViewType,
				rows: mappedRows
			});

			// Set editor content for rich text fields
			setLogicDocContent(taskDashboardFilterById.logicDocumentation || '');
			// Mark data as loaded to render Editor
			setIsDataLoaded(true);
		}
	}, [taskDashboardFilterById, filterTopics?.length, logicOperators?.length]);

	const handleChange = (value, name) => {
		setFormData((prev) => ({
			...prev,
			[name]: value
		}));
		// Remove error when key press
		setErrors((prev) => ({
			...prev,
			[name]: ''
		}));
	};

	const handleRowChange = (index, field, value) => {
		setFormData((prev) => {
			const updatedRows = [...prev.rows];
			const previousValue = updatedRows[index][field];

			updatedRows[index] = {
				...updatedRows[index],
				[field]: value
			};

			// If filterTopic changes (and it's an actual change, not initial load), reset logicExecutors
			// because different topics have completely different executor options
			if (field === 'filterTopic' && previousValue !== null && previousValue !== value) {
				updatedRows[index].logicExecutors = [];
			}

			// If filterLogicOperator changes (and it's an actual change, not initial load), reset logicExecutors
			// because different operators may have different executor options
			if (field === 'filterLogicOperator' && previousValue !== null && previousValue !== value) {
				updatedRows[index].logicExecutors = [];
			}

			return {
				...prev,
				rows: updatedRows
			};
		});

		// Clear row errors when user makes changes
		setErrors((prev) => {
			if (prev?.rows?.[index]) {
				const updatedRowErrors = { ...prev.rows };
				if (updatedRowErrors[index]) {
					delete updatedRowErrors[index][field];
					// If no more errors for this row, remove the row from errors
					if (Object.keys(updatedRowErrors[index]).length === 0) {
						delete updatedRowErrors[index];
					}
				}
				return {
					...prev,
					rows: Object.keys(updatedRowErrors).length > 0 ? updatedRowErrors : undefined
				};
			}
			return prev;
		});
	};

	const handleDeleteRow = (index) => {
		setFormData((prev) => {
			const updatedRows = prev.rows.filter((_, i) => i !== index);
			// Ensure at least 3 rows remain
			while (updatedRows.length < 3) {
				updatedRows.push({
					id: null,
					filterLogicTitle: '',
					filterTopic: null,
					filterLogicOperator: null,
					filterLogicDetails: '',
					logicExecutors: [],
					logicExecutorsInputFieldType: null
				});
			}
			return {
				...prev,
				rows: updatedRows
			};
		});
	};

	const validateForm = () => {
		const newError = {};
		if (!formData.title) {
			newError.title = 'This field is required.';
		}
		if (!formData.stage?.value) {
			newError.stage = 'This field is required.';
		}
		// Check editor content for logicDoc
		const logicDocText =
			logicDocContent === null
				? ''
				: logicDocContent
						.toString()
						.replace(/<[^>]*>/g, '')
						.trim();
		if (!logicDocText) {
			newError.logicDoc = 'This field is required.';
		}

		// Validate Logic item rows - all 4 columns must be filled for a valid row
		const rowErrors = {};
		let hasCompletedRow = false;

		formData.rows.forEach((row, index) => {
			// Check if row has any value
			const hasAnyValue =
				row.filterLogicTitle?.trim() ||
				row.filterTopic ||
				row.filterLogicOperator ||
				(row.logicExecutors && row.logicExecutors.length > 0);

			// If row has any value, all fields must be filled
			if (hasAnyValue) {
				const rowError = {};

				if (!row.filterLogicTitle?.trim()) {
					rowError.filterLogicTitle = 'Title is required';
				}
				if (!row.filterTopic) {
					rowError.filterTopic = 'Topic is required';
				}
				if (!row.filterLogicOperator) {
					rowError.filterLogicOperator = 'Operator is required';
				}

				// If API says inputFieldType is empty string, executors are not applicable.
				const isExecutorsRequired = row.logicExecutorsInputFieldType !== '';
				if (isExecutorsRequired && (!row.logicExecutors || row.logicExecutors.length === 0)) {
					rowError.logicExecutors = 'Logic executor is required';
				}

				// If row has errors, add to rowErrors
				if (Object.keys(rowError).length > 0) {
					rowErrors[index] = rowError;
				} else {
					// Row is complete and valid
					hasCompletedRow = true;
				}
			}
		});

		// Add row errors to main error object
		if (Object.keys(rowErrors).length > 0) {
			newError.rows = rowErrors;
		}

		// Validate Query Logic - only required if there's at least one completed Logic item row
		if (hasCompletedRow) {
			const queryLogicText = formData.queryLogic?.toString().trim() || '';
			if (!queryLogicText) {
				newError.queryLogic = 'This field is required.';
			}
		}

		setErrors(newError);

		return Object.keys(newError)?.length === 0;
	};

	const getPayload = () => {
		// Filter out empty rows and create logicItemRequests
		const logicItemRequests = formData.rows
			.filter((row) => {
				// Only include rows that have at least a title, topic, or operator
				return row.filterLogicTitle || row.filterTopic || row.filterLogicOperator;
			})
			.map((row) => {
				// Parse associatedEntityIds from logicExecutors
				let associatedEntityIds = [];

				if (row.logicExecutors && Array.isArray(row.logicExecutors)) {
					associatedEntityIds = row.logicExecutors
						.map((item) => {
							return item?.value;
						})
						.filter((id) => id !== null);
				}

				return {
					...(row.id && { id: row.id }), // Include id only if it exists (for updates)
					name: row.filterLogicTitle || '',
					topic: row.filterTopic?.id || row.filterTopic?.value || '',
					operator: row.filterLogicOperator?.id || row.filterLogicOperator?.value || '',
					associatedEntityIds
				};
			});

		const payload = {
			title: formData.title,
			categoryId: formData?.stage?.id,
			isActive: formData?.status?.boolean
		};

		// Only include non-empty values
		if (formData?.willCompleteTaskList?.length > 0) {
			payload.taskCompletionAllowedIds = formData.willCompleteTaskList.map((item) => item?.id);
		}

		const logicDoc = logicDocContent || formData.logicDoc;
		if (logicDoc && logicDoc.toString().trim()) {
			payload.logicDocumentation = logicDoc;
		}

		if (formData.queryLogic && formData.queryLogic.toString().trim()) {
			payload.logic = formData.queryLogic;
		}

		if (formData?.leadTime && formData.leadTime.toString().trim()) {
			payload.leadTime = formData.leadTime;
		}

		if (formData?.description && formData.description.toString().trim()) {
			payload.details = formData.description;
		}

		if (logicItemRequests.length > 0) {
			payload.logicItemRequests = logicItemRequests;
		}

		if (formData?.sopDetails?.id) {
			payload.sopId = formData.sopDetails.id;
		}

		const normalizedListViewType =
			formData?.listViewType?.value || formData?.listViewType?.constant || formData?.listViewType;
		if (normalizedListViewType?.toString().trim()) {
			payload.listViewType = normalizedListViewType;
		}

		return payload;
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			return;
		}

		const isEditMode = !!selectedRowToEdit?.id;
		const mutation = isEditMode ? updateTaskDashboardFilter : addTaskDashboardFilter;
		const mutationParams = isEditMode
			? { body: getPayload(), id: selectedRowToEdit.id }
			: { body: getPayload() };

		try {
			// NOTE: RTK Query triggers always resolve; errors can come back as `result.error`.
			// This prevents HTTP 400 from accidentally going down the "success" path.
			const result = await mutation(mutationParams);
			if (result?.error) throw result.error;

			toastSuccess(
				isEditMode
					? 'Task dashboard filter updated successfully'
					: 'Task dashboard filter added successfully'
			);
			resetForm();
			setSelectedRowToEdit?.(null);
			setOpenPrimarySettings(false);
		} catch (err) {
			const errorMessage = getErrorMessage([err])?.[0] || 'An error occurred';
			toastError(errorMessage);

			// If the error is about Query Logic (unknown logic item name / syntax issues), highlight Query Logic field
			const lowerMsg = String(errorMessage).toLowerCase();
			const isQueryLogicError =
				lowerMsg.includes('unknown logic item name') ||
				lowerMsg.includes('query logic') ||
				lowerMsg.includes('missing operator') ||
				lowerMsg.includes('unmatched') ||
				lowerMsg.includes('parentheses') ||
				lowerMsg.includes("'with' expression") ||
				(lowerMsg.includes('operator') &&
					(lowerMsg.includes('missing') || lowerMsg.includes('before')));

			if (isQueryLogicError) {
				setErrors((prev) => ({
					...prev,
					queryLogic: errorMessage
				}));
			}
		}
	};

	const addNewRow = () => {
		setFormData((prev) => ({
			...prev,
			rows: [
				...prev.rows,
				{
					id: null,
					filterLogicTitle: '',
					filterTopic: null,
					filterLogicOperator: null,
					filterLogicDetails: '',
					logicExecutors: [],
					logicExecutorsInputFieldType: null
				}
			]
		}));
	};
	const handleViewFilter = () => {
		window.open(
			`/my-dashboard?tab=MY_DASHBOARD_STYLES&filterId=${selectedRowToEdit?.id}&filterTitle=${encodeURIComponent(formData?.title || '')}&listViewType=${formData?.listViewType || ''}`,
			'_blank'
		);
	};

	return (
		<>
			<div className="task-list-create-container mt-3">
				<div className="task-list-create-wrapper">
					{/* single-task-list */}
					<div className="single-task-list mb-3">
						<div className="task-list-header d-flex align-items-center justify-content-between">
							<h3 className="semibold-14 mb-0">Primary settings</h3>
							<div>
								<button
									className="button outline size32 ml-2"
									onClick={() => {
										setSelectedRowToEdit(null);
										setOpenPrimarySettings(false);
									}}
								>
									Cancel
								</button>
								<button className="button size32 ml-2" onClick={handleSubmit}>
									{selectedRowToEdit?.id ? 'Update' : 'Add'}
								</button>
							</div>
						</div>
						<div className="task-list-create-form mt-3">
							<div className="row">
								<div className="col-md-6">
									<div className="form-group">
										<label htmlFor="list-title">List title*</label>
										<input
											onChange={(e) => handleChange(e.target.value, 'title')}
											value={formData.title}
											type="text"
											name="listTitle"
											id="list-title"
											placeholder="Write your list name"
										/>
										{errors?.title && <p className="error">{errors?.title}</p>}
									</div>
								</div>
								<div className="col-md-6">
									<div className="form-group">
										<label htmlFor="stage">Stage*</label>
										<CustomDropdown
											title="Select stage"
											onItemClick={(value) => handleChange(value, 'stage')}
											selectedItem={formData?.stage?.value}
											items={stageList?.map((item) => ({
												...item,
												id: item?.stageId,
												value: item?.stageName,
												name: item?.stageName
											}))}
										/>
										{errors?.stage && <p className="error">{errors?.stage}</p>}
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group">
										<label htmlFor="list-title">Description</label>
										<input
											onChange={(e) => handleChange(e.target.value, 'description')}
											value={formData.description}
											type="text"
											name="description"
											id="description"
											placeholder="Description"
										/>
									</div>
								</div>
								{selectedRowToEdit?.id && (
									<p
										className="regular-14 gray_dark_01 text-underline cursor-pointer my-2 text-right w-100 pr-3"
										onClick={handleViewFilter}
									>
										View Filter
									</p>
								)}

								<div className="col-md-12">
									<div className="form-group no-shadow-react-select">
										<label htmlFor="stage">Which task can be completed from here</label>
										<CreatableSelect
											isMulti
											value={formData?.willCompleteTaskList}
											onChange={(value) => handleChange(value, 'willCompleteTaskList')}
											options={taskList?.map((item) => ({
												...item,
												id: item?.id,
												value: item?.name,
												label: item?.name
											}))}
											isClearable
											isValidNewOption={() => false}
										/>
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group">
										<label htmlFor="description">Logic documentation*</label>
										<div className="techpack-editor logic-doc-editor mt-3">
											{isDataLoaded && (
												<Editor
													key={`editor-${selectedRowToEdit?.id || 'new'}`}
													setEditorContent={(content) => {
														setLogicDocContent(content);
														handleChange(content, 'logicDoc');
													}}
													setMentionIds={() => {}}
													defaultMention={logicDocContent}
												/>
											)}
											{!isDataLoaded && (
												<div style={{ padding: '10px', color: '#999' }}>
													Loading editor...
												</div>
											)}
										</div>
										{errors?.logicDoc && <p className="error">{errors?.logicDoc}</p>}
									</div>
								</div>
								<div className="col-md-12 pl-1 pr-1">
									<label htmlFor="description" className="pl-2">
										Logic item
									</label>
									<div className="logic-item">
										<table className="table with-border no-hover">
											<tbody>
												{/* Dropdown rows dynamically rendered */}
												{formData.rows.map((row, index) => {
													return (
														<React.Fragment key={row.id || `row-${index}`}>
															<tr>
																<td className="logic-column">
																	<textarea
																		name="filterLogicTitle"
																		placeholder="Custom title"
																		value={row.filterLogicTitle || ''}
																		onChange={(e) => {
																			// Only allow English letters, underscore, and hyphen
																			const sanitizedValue =
																				e.target.value.replace(
																					/[^a-zA-Z_-]/g,
																					''
																				);
																			handleRowChange(
																				index,
																				'filterLogicTitle',
																				sanitizedValue
																			);
																		}}
																	/>

																	{errors?.rows?.[index]
																		?.filterLogicTitle && (
																		<p className="error">
																			{
																				errors.rows[index]
																					.filterLogicTitle
																			}
																		</p>
																	)}
																</td>
																<td className="slect-task-column">
																	<CustomDropdown
																		title="Select topic"
																		selectedItem={row.filterTopic?.value}
																		toolTipText={row.filterTopic?.value}
																		toolTipLength={52}
																		onItemClick={(value) =>
																			handleRowChange(
																				index,
																				'filterTopic',
																				value
																			)
																		}
																		items={filterTopics?.map((item) => ({
																			...item,
																			id: item?.constant,
																			value: item?.name
																		}))}
																	/>
																	{errors?.rows?.[index]?.filterTopic && (
																		<p className="error">
																			{errors.rows[index].filterTopic}
																		</p>
																	)}
																</td>
																<td className="slect-task-column">
																	<CustomDropdown
																		toolTipLength={52}
																		title="Select operator"
																		items={logicOperators?.map(
																			(item) => ({
																				...item,
																				id: item?.constant,
																				name: item?.name,
																				value: item?.name
																			})
																		)}
																		selectedItem={
																			row.filterLogicOperator?.value
																		}
																		onItemClick={(value) =>
																			handleRowChange(
																				index,
																				'filterLogicOperator',
																				value
																			)
																		}
																	/>
																	{errors?.rows?.[index]
																		?.filterLogicOperator && (
																		<p className="error">
																			{
																				errors.rows[index]
																					.filterLogicOperator
																			}
																		</p>
																	)}
																</td>
																<td className="logic-items-column">
																	<LogicExecutorsField
																		row={row}
																		index={index}
																		handleRowChange={handleRowChange}
																		setRowMeta={setRowMeta}
																	/>
																	{errors?.rows?.[index]
																		?.logicExecutors && (
																		<p className="error">
																			{
																				errors.rows[index]
																					.logicExecutors
																			}
																		</p>
																	)}
																</td>
																<td className="action-column">
																	<div className="option">
																		<div className="dropdown">
																			<button
																				className="btn btn-default dropdown-toggle"
																				type="button"
																				id={`menu-${index}`}
																				data-toggle="dropdown"
																			>
																				<CommonImage
																					src="/icons/more_icon.svg"
																					alt="more_icon"
																					width={24}
																					height={24}
																				/>
																			</button>
																			<ul className="dropdown-menu dropdown-menu-right shadow-lg">
																				<li className="px-4 pb-3">
																					<a
																						className="font-weight-normal text-black"
																						style={{
																							cursor: 'pointer'
																						}}
																						onClick={(e) => {
																							e.preventDefault();
																							handleDeleteRow(
																								index
																							);
																						}}
																					>
																						Delete list
																					</a>
																				</li>
																			</ul>
																		</div>
																	</div>
																</td>
															</tr>
														</React.Fragment>
													);
												})}
											</tbody>
											<button className="button outline size32" onClick={addNewRow}>
												Add More
											</button>
										</table>
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group">
										<label htmlFor="queryLogic">Query Logic</label>
										<textarea
											name="queryLogic"
											id="queryLogic"
											placeholder="Enter query logic"
											value={formData.queryLogic}
											onChange={(e) => handleChange(e.target.value, 'queryLogic')}
											rows={6}
											className="query-logic-textarea"
										/>
										{errors?.queryLogic && <p className="error">{errors?.queryLogic}</p>}
									</div>
								</div>
							</div>
						</div>
					</div>
					{/* single-task-list */}
					<div className="single-task-list mb-3">
						<div className="task-list-header d-flex align-items-center justify-content-between">
							<h3 className="semibold-14 mb-0">Others settings</h3>
						</div>
						<div className="task-list-create-form mt-3">
							<div className="row">
								<div className="col-md-6">
									<div className="form-group">
										<label htmlFor="stage">Status</label>
										<CustomDropdown
											title="Select customer"
											onItemClick={(value) => handleChange(value, 'status')}
											selectedItem={formData.status?.value}
											items={FILTER_STATUS}
										/>
									</div>
								</div>
								<div className="col-md-6">
									<div className="form-group">
										<label htmlFor="stage">Lead time</label>
										<input
											name="leadTime"
											type="number"
											placeholder="Input your lead time"
											value={formData.leadTime}
											onChange={(e) => handleChange(e.target.value, 'leadTime')}
										/>
									</div>
								</div>
							</div>
							<div className="row">
								<div className="col-md-6">
									<div className="form-group">
										<label htmlFor="stage">Select SOP and tutorials</label>
										<CustomDropdown
											title="Select"
											onItemClick={(value) => handleChange(value, 'sopDetails')}
											selectedItem={formData.sopDetails?.value}
											items={sopList?.map((item) => ({
												...item,
												id: item?.id,
												value: item?.title,
												name: item?.title
											}))}
										/>
									</div>
								</div>
								<div className="col-md-6 mt-4">
									<p
										className="regular-14 blue text-underline cursor-pointer"
										onClick={() => setSopModalOpen(true)}
									>
										View SOP & tutorials
									</p>
								</div>
								<div className="col-md-6">
									<div className="form-group">
										<label htmlFor="listViewType">Select list type</label>
										<CustomDropdown
											title="Select"
											items={taskListType?.map((item) => ({
												value: item?.name,
												constant: item?.constant
											}))}
											onItemClick={(value) =>
												handleChange(value?.constant || '', 'listViewType')
											}
											selectedItem={listViewTypeSelectedName(
												taskListType,
												formData.listViewType
											)}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			{sopModalOpen && (
				<SopViewModal
					selectedSopId={formData.sopDetails?.id}
					open={sopModalOpen}
					onClose={() => setSopModalOpen(false)}
				/>
			)}
		</>
	);
};

export default PrimarySettings;
